import { Navigate, RouteObject, useLocation } from 'react-router-dom';
import { FORGE_CLEANING_TASK_LIST_PATH, FORGE_SYSTEM_HEALTH_PATH } from './paths';
import { loadRouteChunk } from './utils/lazyRouteModule';

/**
 * Sends a retired task list's bookmark to Cleaning tasks, keeping its chain and, for
 * Crop review issues, its issue type as the review issue filter.
 */
function RetiredTaskListRedirect({ keepIssueType }: { keepIssueType: boolean }) {
  const from = new URLSearchParams(useLocation().search);
  const to = new URLSearchParams();
  const chain = from.get('chain');
  if (chain) to.set('chain', chain);
  const issueType = keepIssueType ? from.get('issue') : null;
  if (issueType) to.set('review', issueType);
  const query = to.toString();
  return <Navigate to={`${FORGE_CLEANING_TASK_LIST_PATH}${query ? `?${query}` : ''}`} replace />;
}

// Page routes load lazily (route.lazy) so each Forge tab is its own chunk and
// the main bundle stays free of Forge page code. The router awaits the chunk
// during navigation, so no Suspense fallback is needed.
export const forgeRoutes: RouteObject = {
  path: '/forge',
  children: [
    { index: true, element: <Navigate to={FORGE_SYSTEM_HEALTH_PATH} replace /> },
    // Dashboards
    {
      path: 'dashboards/system-health',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/SystemHealthDashboard')))
          .SystemHealthDashboard,
      }),
    },
    {
      path: 'dashboards/reported-issues',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/ReportedIssuesDashboard')))
          .ReportedIssuesDashboard,
      }),
    },
    {
      path: 'dashboards/embedding-coverage',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/CoverageDashboard')))
          .CoverageDashboard,
      }),
    },
    {
      path: 'dashboards/pr-inference-okrs',
      lazy: async () => ({
        Component: (
          await loadRouteChunk(
            () => import('./pages/dashboard/ProductRecognitionInferenceDashboard')
          )
        ).ProductRecognitionInferenceDashboard,
      }),
    },
    {
      path: 'dashboards/designs',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/DesignsDashboard')))
          .DesignsDashboard,
      }),
    },
    {
      path: 'dashboards/design-groups',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/DesignGroupsDashboard')))
          .DesignGroupsDashboard,
      }),
    },
    {
      path: 'dashboards/locked-designs',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/LockedDesignsDashboard')))
          .LockedDesignsDashboard,
      }),
    },
    {
      path: 'dashboards/export',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/export/ExportPage'))).ExportPage,
      }),
    },
    {
      path: 'dashboards/recognition-replay',
      lazy: async () => ({
        Component: (
          await loadRouteChunk(() => import('./pages/dashboard/RecognitionReplayDashboard'))
        ).RecognitionReplayDashboard,
      }),
    },
    {
      path: 'dashboards/vizsim-datasets',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/VizSimDatasetsDashboard')))
          .VizSimDatasetsDashboard,
      }),
    },
    {
      path: 'dashboards/scan-assignment',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/ScanAssignmentDashboard')))
          .ScanAssignmentDashboard,
      }),
    },
    {
      path: 'dashboards/model-comparison',
      lazy: async () => ({
        Component: (
          await loadRouteChunk(() => import('./pages/modelComparison/ModelComparisonPage'))
        ).ModelComparisonPage,
      }),
    },
    // Task lists
    {
      path: 'task-lists/store-dc',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/StoreDcDashboard')))
          .StoreDcDashboard,
      }),
    },
    // Retired task lists (DLI-1747): Cleaning tasks carries their review issue and
    // unresolved-crop counts, so old bookmarks land there.
    {
      path: 'task-lists/review-issues',
      element: <RetiredTaskListRedirect keepIssueType />,
    },
    {
      path: 'task-lists/unresolved',
      element: <RetiredTaskListRedirect keepIssueType={false} />,
    },
    {
      path: 'task-lists/cleaning',
      lazy: async () => ({
        Component: (await loadRouteChunk(() => import('./pages/dashboard/CleaningTasksDashboard')))
          .CleaningTasksDashboard,
      }),
    },
    {
      path: 'task-lists/false-detection',
      lazy: async () => ({
        Component: (
          await loadRouteChunk(() => import('./pages/dashboard/FalseDetectionReviewDashboard'))
        ).FalseDetectionReviewDashboard,
      }),
    },
    // Configuration
    {
      path: 'configuration/remote-dc',
      lazy: async () => ({
        Component: (
          await loadRouteChunk(
            () => import('./pages/configuration/RemoteDataCollectionConfiguration')
          )
        ).RemoteDataCollectionConfiguration,
      }),
    },
    {
      path: 'configuration/targeted-dc',
      lazy: async () => ({
        Component: (
          await loadRouteChunk(
            () => import('./pages/configuration/TargetedDataCollectionConfiguration')
          )
        ).TargetedDataCollectionConfiguration,
      }),
    },
    {
      path: 'configuration/recognition-thresholds',
      lazy: async () => ({
        Component: (
          await loadRouteChunk(
            () => import('./pages/configuration/RecognitionThresholdConfiguration')
          )
        ).RecognitionThresholdConfiguration,
      }),
    },
    {
      path: 'configuration/scan-thresholds',
      lazy: async () => ({
        Component: (
          await loadRouteChunk(() => import('./pages/configuration/ScanThresholdConfiguration'))
        ).ScanThresholdConfiguration,
      }),
    },
    // Labeler-accessible routes share the layout in App.tsx with this guarded group.
  ],
};
